import React, { FC, useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useHistory, useParams } from "react-router-dom";
import { SkeletonLoader, Tabs } from "@life-line-lab/components-react";
import { isNil, isEmpty, omitBy, find, set } from "lodash";
import { orderAtom } from "Recoil/order.atom";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import { CloseCircleOutlined } from "@ant-design/icons";
import { Button, BasicModal } from "@life-line-lab/components-react";
import "./parcel-order-view.scss";
import useQuery from "Hooks/query.hook";
import useSession from "Hooks/session.hook";
import { toaster } from "Helpers/toaster";
import orderStatusesMap from "Data/order-statuses.json";
import OrderDetails from "Components/order-management/parcel-order-tab-contents/order-details/order-details";
import DispatchDetails from "Components/order-management/parcel-order-tab-contents/dispatch-details/dispatch-details";
import DeliveryUpdates from "Components/order-management/parcel-order-tab-contents/delivery-updates/delivery-updates";
import OrderCharges from "Components/order-management/parcel-order-tab-contents/order-charges/order-charges";
import OrderNotes from "Components/order-management/parcel-order-tab-contents/order-notes/order-notes";
import FlagRejectParcelModal from "Components/order-management/shared/parcel-modals/flag-reject-parcel";
import ConfirmModal from "Components/order-management/shared/parcel-modals/confirm-modal";
import {
  fetchOrder,
  createOrderNotes,
  createOrderCharges,
  updateOrderNotes,
  updateOrderStatus,
  deleteOrderCharges,
  getOrderDispatchDetails,
  fetchArchiveOrders,
  updateDriverAndHandoverPoint,
  updateOrderDeliveryDate,
  updatePickupDateTime,
} from "Services/order.service";
import {
  addDeliveryUpdates,
  deleteDeliveryUpdates,
  editDeliveryUpdates,
  listDeliveryUpdates,
} from "Services/deliver-updates.services";
import { listOrderVersionHistory } from "Services/order-version-history.services";
import { decode } from "Helpers/enc";
import useAccessControlList from "Hooks/access-control.hook";
import {
  listServicePoints,
  listVehicles,
} from "Services/delivery-settings.services";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import DeliveryDateModal from "Components/order-management/shared/parcel-modals/delivery-date-modal";
import moment from "moment";
import {
  ParcelStatusByPage,
  ParcelStatusIssue,
  ParcelTypeEnums,
} from "Data/order";
import PickupDateModal from "Components/order-management/shared/parcel-modals/pickup-date-modal";

interface useParcelOrderViewPageProps {
  id: string;
  decodedOrderID: string | undefined;
  tabActiveKey: string;
  initialModal: any;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setHideModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleTabClick: (key: string) => Promise<void>;
  setModal: React.Dispatch<React.SetStateAction<any>>;
}

const useParcelOrderViewPage = (props: useParcelOrderViewPageProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const query = useQuery();
  const { roleTypes } = useSession();

  const {
    id,
    decodedOrderID,
    tabActiveKey,
    initialModal,
    setIsLoading,
    setHideModal,
    handleTabClick,
    setModal,
  } = props || {};

  const [orderDetails, setOrderDetails] = useState<any>({});
  const [orderState, setOrderState] = useRecoilState<any>(orderAtom);
  const [deliveryUpdates, setDeliveryUpdates] = useState<any>();
  const [orderDispatch, setOrderDispatch] = useState<any>();
  const [orderVersions, setOrderVersions] = useState<any>();
  const [vehicleOptions, setVehicleOptions] = useState<any>();
  const [sortingHubOptions, setSortingHubOptions] = useState<any>();
  const [error, setError] = useState<any>({});

  const handleOpenParcelOrderEdit = () => {
    const newUrl = new URL(
      `/parcels-order-processing/parcels/${id}/edit`,
      window.location.origin,
    );

    const previousUrl = query.get("from");
    if (previousUrl) {
      newUrl.searchParams.append("from", previousUrl);
    }

    history.push(newUrl.pathname + newUrl.search);
  };

  const fetchOrderDetails = async (orderID: any) => {
    setIsLoading && setIsLoading(true);

    const responseData = await fetchOrder(orderID);
    if (responseData && !responseData.error) {
      setOrderDetails(responseData);
    } else {
      toaster("error", "Error fetching order");
    }

    setIsLoading && setIsLoading(false);
  };

  const fetchArchiveOrderDetails = async (orderID: any) => {
    const responseData = await fetchArchiveOrders(orderID);
    if (responseData && !responseData.error) {
      setOrderDetails(responseData);
    } else {
      toaster("error", "Error fetching order");
    }
  };

  const handleCreateOrderNotes = async (data: any) => {
    const responseData = await createOrderNotes(data, decodedOrderID);
    if (!isNil(responseData) && responseData.orderCode) {
      setHideModal((hideModal) => {
        return !hideModal;
      });
      setOrderDetails(responseData);
      toaster("success", "Created Successfully!");
    } else {
      toaster("error", "Create Failed");
    }
  };

  const handleCreateOrderCharges = async (data: any) => {
    const responseData = await createOrderCharges(data, decodedOrderID);
    if (!isNil(responseData) && responseData.orderCode) {
      setHideModal((hideModal) => {
        return !hideModal;
      });
      setOrderDetails(responseData);
      handleListOrderVersionHistory(decodedOrderID);
      toaster("success", "Created Successfully!");
    } else {
      toaster("error", "Create Failed");
    }
  };

  const handleListDeliveryUpdates = async () => {
    setIsLoading && setIsLoading(true);

    const responseData = await listDeliveryUpdates(
      {
        orderID: orderDetails._id,
        trackingNumber: orderDetails.trackingNumber,
      },
      { sortBy: "createdAt", mode: "ASC" },
    );
    if (!isNil(responseData)) {
      setDeliveryUpdates(responseData);
    } else {
      toaster("error", "Cannot get delivery updates");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleEditDeliveryUpdates = async (data: any) => {
    setIsLoading && setIsLoading(true);

    const responseData = await editDeliveryUpdates(data);
    if (responseData && !responseData.error) {
      handleListDeliveryUpdates();
      toaster("success", "Updated Successfully!");
    } else {
      toaster("error", "Cannot edit delivery updates");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleAddDeliveryUpdates = async (data: any) => {
    setIsLoading && setIsLoading(true);

    const responseData = await addDeliveryUpdates(data);
    if (responseData && !responseData.error) {
      handleListDeliveryUpdates();
      toaster("success", "Updated Successfully!");
    } else {
      toaster("error", "Cannot add delivery updates");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleDeleteDeliveryUpdates = async (data: any) => {
    setIsLoading && setIsLoading(true);

    const responseData = await deleteDeliveryUpdates(data);
    if (responseData && !responseData.error) {
      handleListDeliveryUpdates();
      toaster("success", "Deleted Successfully!");
    } else {
      toaster("error", "Cannot delete delivery updates");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleListOrderDispatch = async () => {
    setIsLoading && setIsLoading(true);

    const responseData = await getOrderDispatchDetails(
      orderDetails._id,
      orderDetails.deliveryTypeName,
    );
    if (!isNil(responseData)) {
      setOrderDispatch(responseData);
    } else {
      toaster("error", "Cannot get order dispatch details");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleListOrderVersionHistory = async (orderID: any) => {
    setIsLoading && setIsLoading(true);

    const responseData = await listOrderVersionHistory(orderID);
    if (responseData && !responseData.error) {
      setOrderVersions(responseData);
    } else {
      toaster("error", "Error fetching order version history");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleUpdateOrderNotes = async (data) => {
    const responseData = await updateOrderNotes(data, decodedOrderID);
    if (!isNil(responseData) && responseData.orderCode) {
      setHideModal((hideModal) => {
        return !hideModal;
      });
      setOrderDetails(responseData);
      handleListOrderVersionHistory(decodedOrderID);
      toaster("success", "Updated Successfully!");
    } else {
      toaster("error", "Update Failed");
    }
  };

  const handleStatusUpdate = async (
    status,
    orderIDs?,
    issueType?,
    remarks?,
  ) => {
    var responseData;
    if (remarks) {
      responseData = await updateOrderStatus(
        orderIDs,
        status,
        issueType,
        remarks,
      );
    } else {
      responseData = await updateOrderStatus(orderIDs, status, issueType);
    }

    if (!isNil(responseData) && responseData.updated) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: "success" },
        list: responseData,
      });
      handleTabClick(tabActiveKey);
      toaster("success", "Order Status updated successfully");
      history.push("/parcels-order-processing/parcels");
    } else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: "error" },
      });
      toaster("error", "Failed to update");
    }
  };

  const handleFlagParcel = (issueType) => {
    handleStatusUpdate(
      "PENDING_CONFIRMATION",
      [decodedOrderID],
      [issueType.issueType],
    );
    setModal(initialModal);
  };

  const handleApproveParcel = () => {
    handleStatusUpdate("PENDING_REVIEW", [decodedOrderID]);
    setModal(initialModal);
  };

  const handleConfirmParcel = () => {
    handleStatusUpdate("DISPATCH_READY", [decodedOrderID]);
    setModal(initialModal);
  };

  const handleRejectParcel = (issueType) => {
    if (roleTypes == "CUSTOMER-SUPER_ADMIN") {
      handleStatusUpdate("CANCELLED", [decodedOrderID], "", issueType.remarks);
    } else {
      handleStatusUpdate("REJECTED", [decodedOrderID], [issueType.issueType]);
    }
    setModal(initialModal);
  };

  const handleDeleteCharges = async (inputCharges, orderID) => {
    const responseData = await deleteOrderCharges(inputCharges.name, orderID);
    if (!isNil(responseData)) {
      setOrderDetails(responseData);
      handleListOrderVersionHistory(decodedOrderID);
      toaster("success", "Deleted Charges Successfully!");
    } else {
      toaster("error", "Delete Failed");
    }
  };

  const handleListingVehicles = async () => {
    const responseData = await listVehicles({ active: true });

    if (responseData && !responseData.error) {
      setVehicleOptions(responseData);
    } else {
      console.log("failed to fetch vehicle options");
    }
  };

  const handleListingSortingHub = async (filters = {}) => {
    const responseData = await listServicePoints(filters);

    if (responseData) {
      setSortingHubOptions(responseData);
      return responseData;
    } else {
      console.log("failed to fetch sorting hub options");
    }
  };

  const handleAssignDriver = async (payload) => {
    const responseData = await updateDriverAndHandoverPoint(
      omitBy(payload, isNil),
    );

    if (responseData && !responseData.error) {
      // setOrderDetails && setOrderDetails(responseData.updatedOrder);
      toaster("success", t("txt.assignedDriverSuccessfully"));
      setTimeout(() => {
        fetchOrderDetails(decodedOrderID);
        handleListOrderDispatch();
      }, 3000); // not a good way to fetch, temp fix
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleUpdatePickupDateTime = async (data) => {
    setIsLoading(true);
    const responseData = await updatePickupDateTime(data);

    if (!responseData.error) {
      setIsLoading(false);
      setOrderDetails((prev) =>
        set(
          { ...prev },
          "firstMileInfoData.pickupData.pickupDate",
          data.pickupDateTime,
        ),
      );
      return true;
    } else {
      setIsLoading(false);
      return false;
    }
  };

  const handleUpdateDeliveryDate = async (data) => {
    setIsLoading(true);

    const responseData = await updateOrderDeliveryDate(data);

    if (!responseData.error) {
      setIsLoading(false);
      setOrderDetails((prev) => ({ ...prev, deliveryDate: data.deliveryDate }));
      return true;
    } else {
      setIsLoading(false);
      return false;
    }
  };

  return {
    error,
    setError,
    sortingHubOptions,
    orderDetails,
    orderState,
    deliveryUpdates,
    orderDispatch,
    orderVersions,
    vehicleOptions,
    handleOpenParcelOrderEdit,
    fetchOrderDetails,
    fetchArchiveOrderDetails,
    handleCreateOrderNotes,
    handleCreateOrderCharges,
    handleListDeliveryUpdates,
    handleEditDeliveryUpdates,
    handleAddDeliveryUpdates,
    handleListOrderDispatch,
    handleListOrderVersionHistory,
    handleUpdateOrderNotes,
    handleStatusUpdate,
    handleFlagParcel,
    handleApproveParcel,
    handleDeleteDeliveryUpdates,
    handleConfirmParcel,
    handleRejectParcel,
    handleDeleteCharges,
    handleListingVehicles,
    handleListingSortingHub,
    handleAssignDriver,
    handleUpdateDeliveryDate,
    handleUpdatePickupDateTime,
  };
};

const ParcelOrderViewPage: FC = () => {
  const { t } = useTranslation();
  const { roleTypes, roles } = useSession();
  let query = useQuery();
  const isArchive = query.get("list");
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const decodedOrderID = decode(id);

  // modal for charges & notes
  const [hideModal, setHideModal] = useState<any>(false);
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Order_Management",
  });

  // modal for every other tab
  const initialModal: any = {
    open: false,
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const showUpdateOption = permission.rights.includes("UPDATE");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tabActiveKey, setTabActiveKey] = useState<string>("0");

  const handleTabClick = async (key: string) => {
    setTabActiveKey(key);
  };

  const {
    sortingHubOptions,
    orderDetails,
    handleEditDeliveryUpdates,
    deliveryUpdates,
    orderDispatch,
    orderVersions,
    vehicleOptions,
    error,
    setError,
    handleOpenParcelOrderEdit,
    fetchOrderDetails,
    fetchArchiveOrderDetails,
    handleCreateOrderNotes,
    handleCreateOrderCharges,
    handleListDeliveryUpdates,
    handleAddDeliveryUpdates,
    handleDeleteDeliveryUpdates,
    handleListOrderDispatch,
    handleListOrderVersionHistory,
    handleUpdateOrderNotes,
    handleFlagParcel,
    handleApproveParcel,
    handleConfirmParcel,
    handleRejectParcel,
    handleDeleteCharges,
    handleListingVehicles,
    handleListingSortingHub,
    handleAssignDriver,
    handleUpdateDeliveryDate,
    handleUpdatePickupDateTime,
  } = useParcelOrderViewPage({
    id,
    decodedOrderID,
    tabActiveKey,
    initialModal,
    setIsLoading,
    setHideModal,
    handleTabClick,
    setModal,
  });

  const onBack = () => {
    const previousUrl: string | undefined = query.get("from") || undefined;
    previousUrl
      ? history.push(decodeURIComponent(previousUrl))
      : history.goBack();
  };

  useEffect(() => {
    if (!isArchive) {
      fetchOrderDetails(decodedOrderID);
      handleListOrderVersionHistory(decodedOrderID);
    } else {
      fetchArchiveOrderDetails(decodedOrderID);
    }
  }, []);

  useEffect(() => {
    const callAPIs = async (status: string) => {
      const isInReview = status === "RETURNED";
      await handleListDeliveryUpdates();
      await handleListOrderDispatch();
      if (isInReview) {
        await handleListingVehicles();
        await handleListingSortingHub({ active: true });
      }
    };

    if (!isEmpty(orderDetails) && !isArchive) {
      callAPIs(orderDetails?.orderStatusInfo?.orderStatus);
    }
  }, [orderDetails]);

  const generateEditButton = () => {
    const isParcelRejectOrCancel = [
      ...ParcelStatusByPage.CANCELLED_ORDERS,
      ...ParcelStatusByPage.REJECTED_ORDERS,
    ].includes(orderDetails?.orderStatusInfo?.orderStatus);

    // Split Shipment:
    // edit button appears at most status with varying stepper contents
    const isSplitShipmentEdit =
      orderDetails.orderType === ParcelTypeEnums.SPLIT &&
      !isParcelRejectOrCancel;

    // For Other Parcel Types:
    // edit button appears at select status with all stepper contents
    const allowEdit = [
      ...ParcelStatusByPage.NEW,
      ...ParcelStatusByPage.CUSTOMER_CONFIRMATION,
      ...ParcelStatusByPage.DIOLKO_REVIEW,
    ].includes(orderDetails?.orderStatusInfo?.orderStatus);

    if (allowEdit || isSplitShipmentEdit) {
      return (
        <div className="dm-edit-button">
          <Button
            onClick={handleOpenParcelOrderEdit}
            className="primary"
            type="primary"
            label={t("action.edit")}
          />
        </div>
      );
    }

    if (showUpdateOption && allowEdit) {
      return (
        <div className="buttons">
          <Button
            onClick={handleOpenParcelOrderEdit}
            className="primary"
            type="primary"
            label={t("action.edit")}
          />
        </div>
      );
    } else {
      return <></>;
    }
  };

  const handleModalAction = (action: string) => {
    switch (action) {
      case "FLAG":
        setCallModal(
          <FlagRejectParcelModal
            action={action}
            orderID={decodedOrderID}
            customer={orderDetails?.senderInfo}
            onSubmit={handleFlagParcel}
            onCancel={handleResetModalState}
          />,
        );
        break;
      case "REJECT":
        setCallModal(
          <FlagRejectParcelModal
            action={action}
            orderID={decodedOrderID}
            customer={orderDetails?.senderInfo}
            onSubmit={handleRejectParcel}
            onCancel={handleResetModalState}
          />,
        );
        break;
      case "CANCEL":
        setCallModal(
          <FlagRejectParcelModal
            action={action}
            orderID={decodedOrderID}
            customer={orderDetails?.senderInfo}
            onSubmit={handleRejectParcel}
            onCancel={handleResetModalState}
          />,
        );
        break;
      case "CONFIRM":
        setCallModal(
          <ConfirmModal
            onSubmit={handleConfirmParcel}
            onCancel={handleResetModalState}
            contentWord={t("txt.confirmParcelQuestion")}
          />,
        );
        break;
      case "APPROVE":
        setCallModal(
          <ConfirmModal
            onSubmit={handleApproveParcel}
            onCancel={handleResetModalState}
            contentWord={t("txt.confirmParcelQuestion")}
          />,
        );
        break;
    }
    setModal({ open: true });
  };

  const StatusButton: React.FC<any> = () => {
    const { orderStatus, issueType } = orderDetails?.orderStatusInfo || {};
    const isCustomer = roles[0]?.type === "CUSTOMER";

    const isInvalidRecipientAddress = find(issueType, (typeObj) => {
      return (
        typeObj.name?.toUpperCase() ===
        ParcelStatusIssue.INVALID_RECIPIENT_ADDRESS
      );
    });

    if (orderStatus === "PENDING_CONFIRMATION")
      return (
        <div className=" dm-action-btn-container">
          <Button
            type="success"
            label={t("action.confirm")}
            onClick={() => {
              handleModalAction("CONFIRM");
            }}
            className="approve-btn"
          />
          <Button
            type={"danger"}
            label={
              roles[0].type == "CUSTOMER"
                ? t("action.cancel")
                : t("action.reject")
            }
            onClick={() => {
              roles[0].type == "CUSTOMER"
                ? handleModalAction("CANCEL")
                : handleModalAction("REJECT");
            }}
            className="flag-btn"
          />
        </div>
      );

    if (orderStatus === "REJECTED" && !isCustomer)
      return (
        <div className="dm-action-btn-container">
          <Button
            type="success"
            label={t("action.approve")}
            onClick={() => {
              handleModalAction("APPROVE");
            }}
            className="approve-btn"
          />
        </div>
      );

    if (["OPEN", "PENDING_REVIEW"].includes(orderStatus) && !isCustomer)
      return (
        <div className="dm-action-btn-container">
          {!isInvalidRecipientAddress && (
            <Button
              type="success"
              label={t("action.confirm")}
              onClick={() => {
                handleModalAction("CONFIRM");
              }}
              className="approve-btn"
            />
          )}
          <Button
            type={
              orderDetails?.orderStatusInfo?.orderStatus == "OPEN"
                ? "primary"
                : "danger"
            }
            label={
              orderDetails?.orderStatusInfo?.orderStatus == "OPEN"
                ? t("action.flag")
                : t("action.reject")
            }
            onClick={() => {
              orderDetails?.orderStatusInfo?.orderStatus == "OPEN"
                ? handleModalAction("FLAG")
                : handleModalAction("REJECT");
            }}
            className="flag-btn"
          />
        </div>
      );

    if (orderStatus === "CANCELLED")
      return (
        <div className="dm-action-btn-container">
          <Button
            type="success"
            label={t("action.accept")}
            onClick={() => {
              handleModalAction("APPROVE");
            }}
            className="approve-btn"
          />
        </div>
      );

    return <></>;
  };

  const handleOnUpdateDeliveryDate = async (data) => {
    const isSuccess = await handleUpdateDeliveryDate(data);

    if (isSuccess) {
      handleResetModalState();
    } else {
      setError((prev) => ({
        ...prev,
        general: "Failed to update delivery date",
      }));
    }
  };

  const handleOnUpdatePickupDateTime = async (data) => {
    const isSuccess = await handleUpdatePickupDateTime(data);

    if (isSuccess) {
      handleResetModalState();
    } else {
      setError((prev) => ({
        ...prev,
        general: "Failed to update delivery date",
      }));
    }
  };

  const modalProps = {
    onClose: handleResetModalState,
    error,
    setError,
    isLoading,
  };

  const handleEditPickupDateTime = (id: string, value: string) => {
    if (!value) return;
    if (!id) return;

    const date = moment(value);

    setCallModal(
      <PickupDateModal
        {...modalProps}
        onSubmit={handleOnUpdatePickupDateTime}
        initialPickupDateTime={date}
        orderID={id}
      />,
    );
    setModal((prev) => ({ ...prev, open: true, orderID: id }));
  };

  const handleEditDeliveryDate = (id: string, value: string) => {
    if (!value) return;
    if (!id) return;

    const date = moment(value);

    setCallModal(
      <DeliveryDateModal
        {...modalProps}
        onSubmit={handleOnUpdateDeliveryDate}
        initialDeliveryDate={date}
        orderID={id}
      />,
    );
    setModal((prev) => ({ ...prev, open: true, orderID: id }));
  };

  //   Tabs
  // =========================================
  const tabs = [
    {
      tabKey: "0",
      label: t("tab.orderDetails"),
      component: (
        <OrderDetails
          orderDetails={orderDetails}
          orderVersion={orderVersions}
          isLoading={isLoading}
          handleEditDeliveryDate={handleEditDeliveryDate}
          handleEditPickupDateTime={handleEditPickupDateTime}
        />
      ),
    },
    {
      tabKey: "1",
      label: t("tab.dispatchDetails"),
      component: (
        <DispatchDetails
          orderDetails={orderDetails}
          orderDispatchDetails={orderDispatch}
          isLoading={isLoading}
          vehicleOptions={vehicleOptions}
          sortingHubOptions={sortingHubOptions}
          handleAssignDriver={handleAssignDriver}
        />
      ),
    },
    {
      tabKey: "2",
      label: t("tab.deliveryUpdates"),
      component: (
        <DeliveryUpdates
          handleAddDeliveryUpdates={handleAddDeliveryUpdates}
          handleEditDeliveryUpdates={handleEditDeliveryUpdates}
          handleListingSortingHub={handleListingSortingHub}
          handleDeleteDeliveryUpdates={handleDeleteDeliveryUpdates}
          deliveryUpdatesInformation={deliveryUpdates}
          isLoading={isLoading}
        />
      ),
    },
    {
      tabKey: "3",
      label: t("tab.orderNotes"),
      component: (
        <OrderNotes
          orderDetails={orderDetails}
          hideModal={hideModal}
          onSubmit={handleCreateOrderNotes}
          onUpdate={handleUpdateOrderNotes}
        />
      ),
    },
    {
      tabKey: "5",
      label: t("tab.estimatedOrderCharges"),
      component: (
        <OrderCharges
          type="estimated"
          hideModal={hideModal}
          onSubmit={handleCreateOrderCharges}
          orderDetails={orderDetails}
          onDeleteCharges={handleDeleteCharges}
          isLoading={isLoading}
        />
      ),
    },
    {
      tabKey: "4",
      label: t("tab.orderCharges"),
      component: (
        <OrderCharges
          type="actual"
          hideModal={hideModal}
          onSubmit={handleCreateOrderCharges}
          orderDetails={orderDetails}
          onDeleteCharges={handleDeleteCharges}
          isLoading={isLoading}
        />
      ),
    },
  ];

  //   Return
  // =========================================
  return (
    <div>
      <Helmet>
        <title>{t("pageTitle.parceOrderProcessing")}</title>
      </Helmet>
      <ModuleTitleBar
        titleText={t("title.parceOrderProcessing")}
        haveSubtitle
        subtitleText={t("title.viewOrder")}
      />
      <div className="dm-put-container">
        <div className="dm-put-header">
          <div className="put-title">
            {orderDetails?.orderCode}
            <span
              className="status-indicator"
              style={getStatusStyle(orderDetails?.orderStatusInfo?.orderStatus)}
            >
              {orderDetails?.orderStatusInfo?.orderStatus
                ? toCapitalisedSpaced(
                    orderStatusesMap?.[
                      orderDetails?.orderStatusInfo?.orderStatus
                    ]?.[roles[0]["type"]],
                  )
                : ""}
            </span>
          </div>
          <div className="put-close">
            <StatusButton />
            {generateEditButton()}
            <CloseCircleOutlined
              style={{ fontSize: "20px" }}
              onClick={() => {
                onBack();
              }}
              className="cursor-pointer back-action right"
            />
          </div>
        </div>
        <div className="dm-divider"></div>
        {isLoading ? (
          <SkeletonLoader type="default" />
        ) : (
          <div className="dm-content">
            <Tabs
              destroyInactiveTabPane={true}
              onTabClick={handleTabClick}
              defaultActiveKey="0"
              activeKey={tabActiveKey}
              tabBarGutter={25}
              tabs={tabs}
              className="tab-css"
            />
          </div>
        )}
      </div>
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={true}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {/*call the state in the basic modal*/}
        {callModal}
      </BasicModal>
    </div>
  );
};

export default ParcelOrderViewPage;
